import React, { useState } from "react";
import axios from "axios";
import Seo from "../components/Seo";
import { PageHero, CTABand } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { useToast } from "../hooks/use-toast";
import { CONTACT } from "../mock";
import { HeartHandshake, Users, CalendarClock, GraduationCap } from "lucide-react";

const BASE = "https://carefirstoh.com";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const perks = [
  { icon: HeartHandshake, title: "Meaningful Work", text: "Make a real difference in the lives of Central Ohio families every day." },
  { icon: CalendarClock, title: "Flexible Scheduling", text: "Full-time, part-time, and flexible shifts that fit your life." },
  { icon: Users, title: "Supportive Team", text: "Join a compassionate, professional team that values its caregivers." },
  { icon: GraduationCap, title: "Growth & Training", text: "Opportunities to learn, grow, and build a rewarding career in home care." },
];
const roles = ["Registered Nurse (RN)", "Licensed Practical Nurse (LPN)", "Home Health Aide (STNA)", "Personal Care Aide", "Companion Caregiver"];
const fieldCls = "w-full border border-gray-200 focus:border-[#4ca82f] focus:ring-1 focus:ring-[#4ca82f] rounded-lg bg-white py-2.5 px-3 text-[14px] outline-none placeholder:text-gray-400";
const labelCls = "block text-[13px] font-semibold text-[#16357a] mb-1";

const Careers = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", role: "", message: "" });
  const [consent, setConsent] = useState(false);
  const setE = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email) { toast({ title: "Missing information", description: "Please enter your name and email.", variant: "destructive" }); return; }
    if (!consent) { toast({ title: "Consent required", description: "Please accept the consent notice.", variant: "destructive" }); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { name: f.name, email: f.email, phone: f.phone, looking_for_job: "Yes", message: `CAREER APPLICATION — Role: ${f.role}. ${f.message}`, consent });
      toast({ title: "Application received!", description: "Thank you for your interest — our team will reach out." });
      setF({ name: "", email: "", phone: "", role: "", message: "" }); setConsent(false);
    } catch { toast({ title: "Something went wrong", description: `Please call ${CONTACT.phone}.`, variant: "destructive" }); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <Seo title="Careers | Caregiver & Nursing Jobs in Columbus, OH | CareFirst" description="Join the CareFirst Home Health team in Columbus, Ohio. Now hiring RNs, LPNs, home health aides (STNA) and caregivers. Flexible scheduling and competitive pay." canonical={BASE + "/careers"} />
      <PageHero title="Build a Career in Caring" subtitle="Join a compassionate team delivering quality home health care across Central Ohio." crumbs={[{ label: "Home", to: "/" }, { label: "Careers" }]} />

      <section className="bg-white py-16">
        <div className="max-w-[1240px] mx-auto px-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {perks.map((p) => (
              <div key={p.title} className="rounded-2xl border border-gray-100 shadow-sm p-7">
                <div className="w-14 h-14 rounded-full bg-[#eef6ea] flex items-center justify-center mb-4"><p.icon size={26} className="text-[#4ca82f]" /></div>
                <h3 className="font-display text-[#16357a] text-[19px] font-semibold mb-2">{p.title}</h3>
                <p className="text-[#666] text-[14px] leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-display text-[#16357a] text-[30px] font-semibold mb-4">Now Hiring</h2>
              <p className="text-[#4a4a4a] text-[16px] leading-relaxed mb-6">We're always looking for caring, dependable professionals to join our growing team. Explore open roles and apply today.</p>
              <ul className="space-y-3">
                {roles.map((r) => (
                  <li key={r} className="flex items-center gap-3 bg-[#eef6ea] rounded-lg px-4 py-3 text-[#16357a] font-semibold text-[15px]"><span className="w-2.5 h-2.5 rounded-full bg-[#4ca82f]" /> {r}</li>
                ))}
              </ul>
            </div>
            <div className="bg-[#eef6ea] rounded-2xl p-7 md:p-9">
              <h3 className="font-display text-[#16357a] text-[24px] font-semibold mb-1">Apply Now</h3>
              <p className="text-[#666] text-[13px] mb-6">Submit your interest and our team will follow up.</p>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={labelCls}>Name *</label><input className={fieldCls} value={f.name} onChange={setE("name")} placeholder="Full name" /></div>
                  <div><label className={labelCls}>Email *</label><input type="email" className={fieldCls} value={f.email} onChange={setE("email")} placeholder="you@email.com" /></div>
                  <div><label className={labelCls}>Phone</label><input className={fieldCls} value={f.phone} onChange={setE("phone")} placeholder="(614) 000-0000" /></div>
                  <div><label className={labelCls}>Position</label>
                    <select className={fieldCls} value={f.role} onChange={setE("role")}>
                      <option value="">Select role</option>
                      {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
                <div><label className={labelCls}>Tell us about yourself</label><Textarea rows={4} className="resize-none border-gray-200 focus-visible:ring-[#4ca82f]" value={f.message} onChange={setE("message")} placeholder="Experience, availability, certifications" /></div>
                <label className="flex items-start gap-2 cursor-pointer"><Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-1 data-[state=checked]:bg-[#4ca82f] data-[state=checked]:border-[#4ca82f]" /><span className="text-[12px] text-gray-500 leading-snug">I consent to be contacted by CareFirst regarding employment. *</span></label>
                <Button type="submit" disabled={loading} className="w-full bg-[#4ca82f] hover:bg-[#3d8a26] text-white font-semibold py-6 rounded-full disabled:opacity-70">{loading ? "Submitting..." : "Submit Application"}</Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
};

export default Careers;
